import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight } from 'lucide-react';
import { graduates } from '@/config/graduates';

// Reusable Graduate/Story Card Component
const StoryCard = ({ graduate }) => {
  return (
    <div 
      data-testid={`graduate-${graduate.id}`}
      className={`bg-white border ${graduate.isSample ? 'border-dashed border-slate-300' : 'border-slate-200'} rounded-lg p-6 hover:shadow-md transition-shadow`}
    >
      {graduate.isSample && (
        <span className="inline-block text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded mb-3">
          Sample Profile
        </span>
      )}
      
      {graduate.photoUrl ? (
        <img
          src={graduate.photoUrl}
          alt={graduate.name}
          className="w-full h-40 object-cover rounded-md mb-4"
        />
      ) : (
        <div className="w-full h-40 bg-slate-100 rounded-md mb-4 flex items-center justify-center">
          <GraduationCap className="w-12 h-12 text-slate-400" />
        </div>
      )}
      
      <h3 className="font-semibold text-slate-900 mb-1">
        {graduate.name}
      </h3>
      <p className="text-sm text-blue-600 font-medium mb-3">
        {graduate.program}
      </p>
      <p className="text-slate-600 text-sm leading-relaxed">
        {graduate.story}
      </p>
      {graduate.graduationYear && (
        <p className="text-xs text-slate-400 mt-4">
          Class of {graduate.graduationYear}
        </p>
      )}
    </div>
  );
};

export default function GraduatesPage() {
  const sampleGraduates = graduates.filter(g => g.isSample);
  const realGraduates = graduates.filter(g => !g.isSample);
  const hasRealGraduates = realGraduates.length > 0;

  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="graduates-hero"
        className="relative pt-28 pb-16 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Impact Stories
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
              Graduates & Success Stories
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              In the future, we'll showcase the families, seniors, and organizations 
              whose lives have been improved by our work.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section data-testid="graduates-list" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          {hasRealGraduates ? (
            <>
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  Our Graduates
                </h2>
                <p className="text-slate-600">
                  Meet the incredible individuals who have benefited from our programs.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {realGraduates.map((graduate) => (
                  <StoryCard key={graduate.id} graduate={graduate} />
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Future-facing content */}
              <div className="max-w-3xl mx-auto text-center mb-12">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <GraduationCap className="w-10 h-10 text-blue-600" />
                </div>
                <h2 
                  data-testid="graduates-coming-soon"
                  className="text-3xl font-bold text-slate-900 mb-4"
                >
                  Success Stories Coming Soon
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  As we grow our scholarship programs and educational initiatives, 
                  this page will celebrate the students and families whose lives 
                  have been transformed through our support.
                </p>
              </div>

              {/* Sample Cards Preview */}
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <p className="text-sm text-slate-500">
                    Here's how graduate profiles will appear:
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {sampleGraduates.map((graduate) => (
                    <StoryCard key={graduate.id} graduate={graduate} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* What Each Profile Includes */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-xl font-semibold text-slate-900 mb-6">
              What Each Profile Will Include
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <p className="font-medium text-slate-800 text-sm">Name & Photo</p>
                <p className="text-xs text-slate-500">The graduate's identity and portrait</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <p className="font-medium text-slate-800 text-sm">Program / Initiative</p>
                <p className="text-xs text-slate-500">Which program they participated in</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <p className="font-medium text-slate-800 text-sm">Their Story</p>
                <p className="text-xs text-slate-500">A short paragraph about their journey</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <p className="font-medium text-slate-800 text-sm">Impact</p>
                <p className="text-xs text-slate-500">How the foundation helped them</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="graduates-cta" className="py-16 bg-blue-700">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Support Future Graduates
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto mb-8">
            Your support helps provide scholarships and educational resources 
            to students who dream of a brighter future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/mission"
              data-testid="graduates-learn-more"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 hover:bg-slate-100 rounded-md px-6 py-3 font-medium transition-colors"
            >
              Learn About Our Mission
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="graduates-contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 rounded-md px-6 py-3 font-medium transition-colors"
            >
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
